import {
  forwardRef,
  type InputHTMLAttributes,
  type TextareaHTMLAttributes,
  type ReactNode,
} from "react";
import { cn } from "@/lib/cn";

const baseInput =
  "w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 " +
  "focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 " +
  "disabled:cursor-not-allowed disabled:bg-zinc-50 disabled:text-zinc-400";

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...rest }, ref) {
    return <input ref={ref} className={cn(baseInput, "h-10", className)} {...rest} />;
  },
);

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(function Textarea({ className, ...rest }, ref) {
  return (
    <textarea ref={ref} className={cn(baseInput, "min-h-[120px] resize-y", className)} {...rest} />
  );
});

interface LabelProps {
  htmlFor?: string;
  required?: boolean;
  hint?: string;
  children: ReactNode;
}

export function Label({ htmlFor, required, hint, children }: LabelProps): JSX.Element {
  return (
    <div className="flex items-center justify-between">
      <label htmlFor={htmlFor} className="text-sm font-medium text-zinc-700">
        {children}
        {required ? <span className="ml-0.5 text-red-500">*</span> : null}
      </label>
      {hint ? <span className="text-xs text-zinc-400">{hint}</span> : null}
    </div>
  );
}

interface FieldProps {
  id: string;
  label: ReactNode;
  required?: boolean;
  hint?: string;
  error?: string | null;
  children: ReactNode;
}

export function Field({ id, label, required, hint, error, children }: FieldProps): JSX.Element {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} required={required} hint={hint}>
        {label}
      </Label>
      {children}
      {error ? <p className="text-xs text-red-600">{error}</p> : null}
    </div>
  );
}
